import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function ReadingCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Reading Pothole Claims | 79% Residential Roads Green | Fixtyer"
        description="Reading Borough Council lifted 79% of residential roads to green condition after £17M of its own capital — yet safety defects nearly doubled in 2023/24 and 2024/25, and the DfT flags incomplete condition data. See what the AMBER rating means for your claim."
        canonical="/councils/reading"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Reading', url: '/councils/reading' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Reading spends more than double its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Reading projects £5.806m capital spend against a £2.77m DfT allocation in 2025/26, with an estimated 90% preventative share. But the overall and condition scorecards are AMBER, and the DfT notes Reading\'s ratings are based on incomplete road condition data. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend.',
          },
          {
            question: 'What does the DfT incomplete road condition data flag mean for my claim?',
            answer: 'The Department for Transport marks Reading with *** in its 2025/26 ratings table, noting that overall, condition and best-practice scorecards are "based on incomplete road condition data." Reading\'s own transparency report also admits a 2018–2022 Berkshire survey contract produced quality issues that "will have skewed the local classified road results." That weakens any blanket Section 58 defence built on network-wide condition records.',
          },
          {
            question: 'If 79% of residential roads are green, does that weaken my pothole claim?',
            answer: 'Not necessarily — and the headline needs context. Reading measures U-road condition through coarse visual inspections (CVI), not the same SCANNER surveys used on classified roads. The council\'s ASOR (March 2025) states 21% of unclassified roads still need treatment, clusters of defects persist on un-resurfaced routes, and future funding at £500k is "less than half the level required to prevent deterioration." Your claim lives on the specific road and defect, not borough-wide percentages.',
          },
          {
            question: 'Reading repaired fewer potholes in 2024/25 — are the roads fixed?',
            answer: 'Reading filled 1,590 potholes in 2024/25 — down from 2,055 in 2023/24 and a peak of 3,680 in 2019/20. The council attributes lower counts to its £17M capital programme reducing defects forming in the first place. Yet its own ASOR records almost 400 safety defects in both 2023/24 and 2024/25 — nearly double 2022/23 — and roughly 3,000 maintenance defects in three of the last four years.',
          },
          {
            question: 'Does Reading defending 83% of settled claims mean I should not bother?',
            answer: 'No. The council\'s Carriageway ASOR (March 2025) states it defended 83% of settled third-party claims in 2024/25 — meaning roughly one in six settled claims resulted in compensation. That is not a reason to avoid claiming; it shows claims do succeed when evidence is strong. Prior reports, photos showing defect age, and Reading\'s own admissions about classified-road deterioration and survey gaps strengthen specific cases.',
          },
          {
            question: 'What if my pothole was on an A-road like the A33 or Oxford Road?',
            answer: 'Classified roads make up 105.8km — about 27% of Reading\'s 397.4km network. After years of improvement, Reading\'s ASOR confirms classified roads deteriorated in 2024/25 because "very little work was carried out" between 2023 and 2024 surveys while a two-year contract was prepared. A-road green condition fell from 63.1% in 2021 to 57.6% in 2024. If your damage was on a main road during that gap, the council\'s own explanation is relevant to Section 58.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Reading Borough Council"
        councilSlug="reading"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/reading"
        headline="Reading Borough Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Reading</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Reading: 79% of residential roads green — on data the DfT calls incomplete
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Reading Borough Council invested <strong className="text-emerald-400">£17 million of its own capital</strong> in
            roads and footways since 2020/21, lifting unclassified roads from{' '}
            <strong className="text-emerald-400">35% to 79% green condition</strong>. The DfT Spend scorecard is GREEN
            and overall is <strong className="text-amber-400">AMBER</strong> — but the department flags Reading&apos;s
            ratings as <strong className="text-rose-400">based on incomplete road condition data</strong>, while the
            council&apos;s own ASOR records{' '}
            <strong className="text-rose-400">almost 400 safety defects in each of 2023/24 and 2024/25</strong>, nearly
            double the prior year.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">79%</div>
            <div className="text-white font-medium mt-2">Residential roads in green condition (2025/26)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 35% in 2019/20 after 101.146km of U-road surfacing across 900+ local streets — yet 21% still
              need treatment and the DfT cannot fully verify the condition picture.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard caveat */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The DfT scorecards — and the incomplete-data asterisk
          </h2>
          <p className="text-surface-600 mb-8">
            Department for Transport local road maintenance ratings 2025/26, with Reading&apos;s published transparency
            report (27 June 2025)
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Overall</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Condition</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
              <RatingBadge rating="green" size="md" />
              <div className="text-sm font-semibold text-emerald-900 mt-3">Spend</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Best practice</div>
            </div>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-3">DfT caveat (Reading***)</h3>
            <p className="text-rose-800 text-sm mb-3">
              The Department for Transport notes that Reading&apos;s overall rating and its condition and best-practice
              scorecards are <strong>&ldquo;based on incomplete road condition data, which has affected these
              ratings.&rdquo;</strong> That is the government&apos;s characterisation — not ours.
            </p>
            <p className="text-rose-800 text-sm">
              Reading&apos;s own transparency report adds that between 2018 and 2022, a shared Berkshire survey contract
              had &ldquo;issues with the quality of the surveys produced by the contractor which will have skewed the
              local classified road results&rdquo; — meaning the council &ldquo;will have been working with inaccurate
              information when planning its resurfacing programmes.&rdquo;
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The network in numbers</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">397.4km</div>
                <div className="text-brand-300 text-sm">Public highway roads maintained</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">291.6km</div>
                <div className="text-brand-300 text-sm">Unclassified (residential) roads — 73% of network</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">900+</div>
                <div className="text-brand-300 text-sm">Local roads improved since 2020/21</div>
              </div>
            </div>
            <p className="text-brand-200 text-sm">
              Breakdown from Reading&apos;s transparency report: 36.9km A-roads, 68.9km B and C roads, 291.6km U-roads.
              The council also maintains 1,027km of footways, 80 bridges and 18,844 lighting units.
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER survey results for classified roads and CVI inspections for residential streets — from Reading&apos;s
            Local highways maintenance transparency report (27 June 2025)
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (36.9km): green share falling since 2021</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">32.2%</td>
                    <td className="px-4 py-2 text-center">61.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4.6%</td>
                    <td className="px-4 py-2 text-center">32.2%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">63.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.7%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">37.8%</td>
                    <td className="px-4 py-2 text-center">56.5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.5%</td>
                    <td className="px-4 py-2 text-center">35.9%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">57.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road red condition rose from 4.6% in 2021 to 6.5% in 2024, while green fell from 63.1% to 57.6%. The
              council notes &ldquo;minor deterioration&rdquo; on classified roads and is mid-way through a two-year A–C
              investment programme.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (68.9km): red share improved, amber fell</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.5%</td>
                    <td className="px-4 py-2 text-center">23.23%</td>
                    <td className="px-4 py-2 text-center">74.27%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.69%</td>
                    <td className="px-4 py-2 text-center">29.62%</td>
                    <td className="px-4 py-2 text-center">64.69%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2.79%</td>
                    <td className="px-4 py-2 text-center">20.64%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">76.57%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 text-sm">
              B and C roads show the strongest classified-network improvement — red down from a 2022 peak of 5.69% to
              2.79% in 2024. That helps the council on main distributor routes, but remember the DfT incomplete-data flag
              and the 2018–2022 survey quality admission.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The residential transformation — and what it leaves out</h3>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-4xl font-bold text-emerald-400">35% → 79%</div>
                <div className="text-brand-300 text-sm mt-1">U-road green condition (2019/20 to 2025/26)</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-amber-400">21%</div>
                <div className="text-brand-300 text-sm mt-1">U-roads still requiring treatment (ASOR, March 2025)</div>
              </div>
            </div>
            <p className="text-brand-200 text-sm mb-4">
              Reading prioritised 291.6km of previously &ldquo;neglected&rdquo; residential streets, surfacing 101.146km
              of U-roads and improving 900+ local roads. Of the remaining 21% needing treatment,{' '}
              <strong className="text-white">less than 1% is in red</strong> — but investigations suggest red areas are
              often localised clusters of potholes rather than whole road lengths.
            </p>
            <p className="text-brand-300 text-sm">
              U-road condition is monitored through coarse visual inspections (CVI) by highway inspectors — a different
              methodology from SCANNER laser surveys on classified roads. The transparency report&apos;s U-road RED
              category table shows 0% each year 2020–2024, which reflects that specific metric, not an claim that
              residential streets are defect-free.
            </p>
          </div>
        </div>
      </section>

      {/* Defects and potholes */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Safety defects, maintenance defects and pothole repairs
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s Carriageway ASOR summary (March 2025) and transparency report repair tables
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-5xl font-bold text-rose-600">~400</div>
              <div className="text-rose-900 font-medium mt-2">Safety defects per year</div>
              <p className="text-rose-800 text-sm mt-3">
                Almost 400 safety defects were recorded in both 2023/24 and 2024/25 — almost double the amount in
                2022/23. Despite resurfacing investment, &ldquo;significant levels of defects exist on the roads that
                have not been resurfaced recently with clusters of defects on some roads.&rdquo;
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-5xl font-bold text-amber-600">~3,000</div>
              <div className="text-amber-900 font-medium mt-2">Maintenance defects (typical year)</div>
              <p className="text-amber-800 text-sm mt-3">
                The quantity of maintenance defects recorded in three of the last four years has been approximately
                3,000. Customer contacts about carriageways run 600–800 annually; 2024/25 reported 760 — within the
                expected range.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Notes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2016/17</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5,030</td>
                  <td className="px-6 py-4 text-surface-600">Historical peak cited in transparency report</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2019/20</td>
                  <td className="px-6 py-4 text-center">3,680</td>
                  <td className="px-6 py-4 text-surface-600">Pre-£17M programme baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,236</td>
                  <td className="px-6 py-4 text-surface-600">Capital programme begins</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">1,328</td>
                  <td className="px-6 py-4 text-surface-600">Lowest year in five-year table</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,242</td>
                  <td className="px-6 py-4 text-surface-600">Continued decline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,055</td>
                  <td className="px-6 py-4 text-surface-600">94% of 40mm+ potholes repaired within 28 days (Corporate KPI)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">1,590</td>
                  <td className="px-6 py-4 text-surface-600">Council cites capital programme reducing formations</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Despite the investment made in resurfacing over recent years significant levels of defects exist on the roads that have not been resurfaced recently with clusters of defects on some roads."
            source="Reading Borough Council — Carriageway ASOR summary (March 2025)"
          />
        </div>
      </section>

      {/* Spend */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN spend — and the funding cliff ahead
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital and revenue maintenance spending from Reading&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£2.335m</td>
                  <td className="px-4 py-3 text-center">£3.472m</td>
                  <td className="px-4 py-3 text-center">£1.281m</td>
                  <td className="px-4 py-3 text-center">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£1.838m</td>
                  <td className="px-4 py-3 text-center">£7.198m</td>
                  <td className="px-4 py-3 text-center">£2.212m</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£1.838m</td>
                  <td className="px-4 py-3 text-center">£7.092m</td>
                  <td className="px-4 py-3 text-center">£2.552m</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£1.838m</td>
                  <td className="px-4 py-3 text-center">£7.015m</td>
                  <td className="px-4 py-3 text-center">£2.550m</td>
                  <td className="px-4 py-3 text-center">90%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£2.77m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£5.806m</td>
                  <td className="px-4 py-3 text-center">£2.633m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">90%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">The £17M own-capital programme</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Beyond DfT Local Transport Plan awards, Reading invested £17M of council capital from 2020/21 to
                2026/27 — plus a further £8m residential resurfacing programme referenced on the council&apos;s road
                improvements page (following a completed £9m scheme).
              </p>
              <p className="text-emerald-800 text-sm">
                Delivered work includes 29.746km of classified resurfacing, 101.146km of U-road surfacing, 1.6km of
                Rhinophalt preservation on the A33, and 175+ strengthened footpaths.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Future funding warning</h3>
              <p className="text-rose-800 text-sm mb-3">
                Reading&apos;s Carriageway ASOR states future investment at{' '}
                <strong>£500k is less than half the level required to prevent deterioration</strong>. Over time,
                &ldquo;the benefit of recent investment will be eroded and a consequential increase in roads in poor
                and deteriorating condition can be expected.&rdquo;
              </p>
              <p className="text-rose-800 text-sm">
                The same report anticipates future funding &ldquo;will be insufficient to prevent deterioration&rdquo;
                on classified roads even as 2025/26 schemes complete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Classified deterioration admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The classified-road deterioration admission
          </h2>
          <p className="text-surface-600 mb-8">
            Reading&apos;s own explanation for slipping main-road condition — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="After years of improvement the classified roads have deteriorated in 2024/25. Very little work was carried out on classified roads between the 2023 and 2024 surveys in terms of resurfacing and surface treatment. This was due to the preparation of a 2 year contract."
              source="Reading Borough Council — Carriageway ASOR summary (March 2025)"
            />
            <QuoteCard
              quote="Future funding however is anticipated to be insufficient to prevent deterioration."
              source="Reading Borough Council — Carriageway ASOR summary (March 2025)"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Reading must show a reasonable system for knowing and repairing road
              defects. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road one of the 21% of U-roads still needing treatment — or in a localised red cluster?</li>
              <li>• If classified roads deteriorated during a contract-preparation gap, was your A or B road inspected adequately?</li>
              <li>• Does the DfT incomplete-data flag undermine network-wide condition claims?</li>
              <li>• Did prior reports (FixMyStreet, council portal) put the council on notice before your incident?</li>
              <li>• Reading defends 83% of settled claims — what specific evidence separates the 17% that succeed?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot point to borough-wide green percentages while its own ASOR documents survey gaps,
              defect clusters and insufficient future funding.
            </p>
          </div>
        </div>
      </section>

      {/* Claims analysis */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What this means for your claim
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Reading is not Slough — but AMBER with incomplete data is not automatic rejection either
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — more than double DfT allocation in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 90% preventative maintenance projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 79% U-road green condition after documented £17M programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 99% pothole response-time compliance on R1/R2 priorities (transparency report)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> NHT survey: 29 of 30 highway maintenance indicators above national average (2024/25)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on recently resurfaced residential streets with documented inspection regimes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT flags incomplete road condition data for overall, condition and best-practice scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2018–2022 SCANNER survey quality issues skewed classified-road planning data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~400 safety defects in 2023/24 and 2024/25 — nearly double 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Classified roads deteriorated in 2024/25 during a works-programme gap
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Future funding at £500k — less than half needed to prevent deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 21% of U-roads still need treatment; defect clusters on un-resurfaced routes
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a genuine residential-road improvement story, your claim lives or
              dies on the <strong className="text-white">specific defect and road</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council portal) — proof of actual notice</li>
              <li>• Photos showing size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — CVI-inspected U-roads vs SCANNER-surveyed A/B/C routes carry different evidential angles</li>
              <li>• Whether your street was in the 21% still awaiting treatment or a documented defect cluster</li>
              <li>• Timing relative to the 2023–2024 classified-road maintenance gap</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Reading&apos;s own transparency and ASOR data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Reading first
          </h2>
          <p className="text-surface-600 mb-6">
            Reading prioritises defects deeper than 40mm, wider than 300mm, with a vertical edge — but all reports create
            a dated record. Response targets range from 3 hours to 28 days depending on risk classification. Keep your
            reference number even if the defect does not immediately meet repair criteria.
          </p>
          <a
            href="https://www.reading.gov.uk/vehicles-roads-and-transport/roads-and-streets/report-a-road-or-street-problem/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a road or street problem to Reading Borough Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            For trunk roads and motorways (M4, parts of A33), report to National Highways instead. Reading repairs
            defects using risk-based priorities — not every reported pothole will meet the 40mm/300mm threshold.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Reading?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded AMBER council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No DfT incomplete-data argument</li>
                <li>• No classified-road deterioration gap cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 79% U-road headline challenged with ASOR context</li>
                <li>✅ Incomplete DfT data flag and survey-quality admission cited</li>
                <li>✅ ~400 safety defects and classified-road gap documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Reading</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=reading"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Reading claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Reading spends more than double its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Reading projects £5.806m capital spend against a £2.77m
                DfT allocation in 2025/26, with an estimated 90% preventative share. But the overall and condition
                scorecards are AMBER, and the DfT notes Reading&apos;s ratings are based on incomplete road condition
                data. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected
                and repaired — not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the DfT incomplete road condition data flag mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The Department for Transport marks Reading with *** in its 2025/26 ratings table, noting that overall,
                condition and best-practice scorecards are &ldquo;based on incomplete road condition data.&rdquo;
                Reading&apos;s own transparency report also admits a 2018–2022 Berkshire survey contract produced quality
                issues that &ldquo;will have skewed the local classified road results.&rdquo; That weakens any blanket
                Section 58 defence built on network-wide condition records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                If 79% of residential roads are green, does that weaken my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — and the headline needs context. Reading measures U-road condition through coarse
                visual inspections (CVI), not the same SCANNER surveys used on classified roads. The council&apos;s ASOR
                (March 2025) states 21% of unclassified roads still need treatment, clusters of defects persist on
                un-resurfaced routes, and future funding at £500k is &ldquo;less than half the level required to prevent
                deterioration.&rdquo; Your claim lives on the specific road and defect, not borough-wide percentages.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Reading repaired fewer potholes in 2024/25 — are the roads fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                Reading filled 1,590 potholes in 2024/25 — down from 2,055 in 2023/24 and a peak of 3,680 in 2019/20.
                The council attributes lower counts to its £17M capital programme reducing defects forming in the first
                place. Yet its own ASOR records almost 400 safety defects in both 2023/24 and 2024/25 — nearly double
                2022/23 — and roughly 3,000 maintenance defects in three of the last four years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Reading defending 83% of settled claims mean I should not bother?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council&apos;s Carriageway ASOR (March 2025) states it defended 83% of settled third-party claims
                in 2024/25 — meaning roughly one in six settled claims resulted in compensation. That is not a reason
                to avoid claiming; it shows claims do succeed when evidence is strong. Prior reports, photos showing
                defect age, and Reading&apos;s own admissions about classified-road deterioration and survey gaps
                strengthen specific cases.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an A-road like the A33 or Oxford Road?
              </h3>
              <p className="text-surface-600 text-sm">
                Classified roads make up 105.8km — about 27% of Reading&apos;s 397.4km network. After years of
                improvement, Reading&apos;s ASOR confirms classified roads deteriorated in 2024/25 because &ldquo;very
                little work was carried out&rdquo; between 2023 and 2024 surveys while a two-year contract was prepared.
                A-road green condition fell from 63.1% in 2021 to 57.6% in 2024. If your damage was on a main road
                during that gap, the council&apos;s own explanation is relevant to Section 58.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.reading.gov.uk/vehicles-roads-and-transport/roads-and-streets/residential-road-improvements/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Reading Borough Council — Local highways maintenance transparency report (27 June 2025)
            </a>{' '}
            |{' '}
            <a
              href="https://images.reading.gov.uk/2025/11/RBC-Transparency-Incentive-Funding-Appendix-B-Reponse.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Reading Borough Council — Transparency report Appendix B (31 October 2025)
            </a>{' '}
            |{' '}
            <a
              href="https://democracy.reading.gov.uk/mgConvert2PDF.aspx?ID=37517"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Reading Borough Council — Carriageway ASOR summary (March 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
